import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "./Header";
import { api } from "../server";

function CorporateApprovals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState({});

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/api/admin/customers?type=corporate`);
      setRows(data?.customers || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.corporateStatus === filter);
  }, [rows, filter]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      pending: rows.filter((r) => r.corporateStatus === "pending").length,
      approved: rows.filter((r) => r.corporateStatus === "approved").length,
      rejected: rows.filter((r) => r.corporateStatus === "rejected").length,
    }),
    [rows],
  );

  const setStatus = async (id, status) => {
    setBusy((s) => ({ ...s, [id]: true }));
    try {
      const data = await api.put(`/api/admin/customers/${id}`, {
        corporateStatus: status,
      });
      const updated = data?.customer;
      setRows((prev) =>
        prev.map((x) => (x._id === id ? { ...x, ...updated } : x)),
      );
    } catch (err) {
      alert(err.message || "Update failed");
    } finally {
      setBusy((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <section className="container-fluid p-4 pt-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-0 lh-1 ls-sm fw-normal">
                  Corporate Approvals
                </h3>
                <div className="text-secondary lh-1">
                  Review & approve corporate customer accounts
                </div>
              </div>
              <button
                type="button"
                className="btn btn-light border px-4"
                onClick={fetchRows}
              >
                <i className="bi bi-arrow-clockwise me-2" />
                Refresh
              </button>
            </div>

            <div className="row g-2 mb-2">
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-building" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Total</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.total}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-warning-subtle text-warning"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-hourglass-split" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Pending</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.pending}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-check2-circle" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Approved</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.approved}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-danger-subtle text-danger"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-x-circle" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Rejected</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.rejected}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-3 p-md-4">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-funnel" />
                    </div>
                    <div>
                      <div className="fw-semibold lh-1">Filter by status</div>
                      <div className="text-secondary small">
                        Showing: <strong>{filter}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="btn-group">
                    {["pending", "approved", "rejected", "all"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`btn px-4 ${filter === s ? "btn-primary text-white" : "btn-outline-secondary border-light bg-white text-dark"}`}
                        onClick={() => setFilter(s)}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4 mt-2">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-5 text-secondary">
                    <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />
                    No corporate customers
                  </div>
                ) : (
                  <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table className="table mb-0 text-nowrap table-centered">
                      <tbody>
                        {filtered.map((r, index) => (
                          <tr key={r._id}>
                            <td>{index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Company</span> &mdash;{" "}
                              {r.companyName || "—"}
                              <br />
                              <span className="text-dark">
                                Contact
                              </span> &mdash; {r.name}
                              <br />
                              <span className="text-dark">
                                Phone
                              </span> &mdash; {r.phone}
                              <br />
                              <span className="text-dark">
                                Email
                              </span> &mdash; {r.email || "—"}
                              <br />
                              <span className="text-dark">
                                GST
                              </span> &mdash; {r.gstNumber || "—"}
                              <br />
                              <span className="text-dark">
                                Status
                              </span> &mdash;{" "}
                              <span
                                className={
                                  r.corporateStatus === "approved"
                                    ? "text-success"
                                    : r.corporateStatus === "pending"
                                      ? "text-warning"
                                      : "text-danger"
                                }
                              >
                                {r.corporateStatus}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 align-items-start">
                                {r.corporateStatus !== "approved" && (
                                  <span
                                    onClick={() =>
                                      !busy[r._id] &&
                                      setStatus(r._id, "approved")
                                    }
                                    className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    <i className="bi bi-check2 me-1" />
                                    Approve
                                  </span>
                                )}
                                {r.corporateStatus !== "rejected" && (
                                  <span
                                    onClick={() =>
                                      !busy[r._id] &&
                                      setStatus(r._id, "rejected")
                                    }
                                    className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    <i className="bi bi-x-lg me-1" />
                                    Reject
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

export default CorporateApprovals;
